import { useContext, useEffect, useRef } from 'react';
import { validateFormField } from '../Core/FormFieldValidation';
import { SmartContext } from '../Core/SmartContext';
import { evaluateExpression, getControlValueFromState, handleControlValueChange } from '../Core/SmartFunctions';
import { DomainElement, SimpleFormControlArguments, State } from '../Core/SmartTypes';
import ErrorControl from './ErrorControl';
import { v4 as uuidv4 } from 'uuid';

const SelectGroupedControl = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, dataKey, parentDataKey, handleChangeEvent } = { ...args };

    const domainCategoryCode = control.props.domainCategoryCode;
    const groupDomainCategoryCode = control.props.groupDomainCategoryCode;

    const readOnly =
        control.readOnly ||
        evaluateExpression(control.readOnlyExpression, state?.data, getControlValueFromState(parentDataKey as string, state as State));

    const data = getControlValueFromState(dataKey, state as State);

    const formControlRef = useRef(null);

    const options: DomainElement[] = (state?.domain?.get(domainCategoryCode) as DomainElement[]) ?? [];
    const groups: DomainElement[] = (state?.domain?.get(groupDomainCategoryCode) as DomainElement[]) ?? [];

    useEffect(() => {
        const errorMessages = validateFormField(
            control,
            data,
            state,
            control?.props?.label,
            dataKey,
            getControlValueFromState(parentDataKey as string, state as State)
        );
        dispatch({ type: 'SET_FIELD_VALIDATION_ERRORS', payload: { dataKey, errorMessages } });
    }, []);

    const getGroupedOptions = () => {
        if (groups.length === 0) {
            return options.map((opt, idx) => (
                <option key={`${opt.code}-${idx}`} value={opt.code}>
                    {opt.value}
                </option>
            ));
        }

        return groups.map((group) => {
            const children = options.filter((opt) => opt.parentCode === group.code);
            if (children.length === 0) return null;
            return (
                <optgroup key={group.code} label={group.value}>
                    {children.map((opt, idx) => (
                        <option key={`${opt.code}-${idx}`} value={opt.code}>
                            {opt.value}
                        </option>
                    ))}
                </optgroup>
            );
        });
    };

    return (
        <>
            {control.props.label && (
                <label htmlFor={control.id} className="form-label w-100">
                    {`${control.props.label} `}
                </label>
            )}
            <select
                key={uuidv4()}
                id={control.id}
                name={dataKey}
                className="form-select"
                value={data ?? ''}
                required={control.props.required}
                disabled={readOnly ?? false}
                onChange={(event) =>
                    handleControlValueChange({ control, value: event.target.value, dataKey, parentDataKey, state, dispatch })
                }
                ref={formControlRef}>
                <option value="">{control.props.placeholder ?? 'Select'}</option>
                {getGroupedOptions()}
            </select>
            <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />
        </>
    );
};

export default SelectGroupedControl;
